import "../Overview/EndpointOverview.css";
import "./TenantDetails.css";
import { Fragment, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import {
    Building2,
    Users,
    Monitor,
    Key,
    Trash2,
    Copy,
    Eye,
    EyeOff,
    Check,
    Clock,
} from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../ui/alert-dialog";
import { useToast } from "../../../hooks/use-toast";
import { TenantData } from "./interface";
import useUserStore from "../../../services/userStore";
import { deleteTenant, fetchTenants, generateApiKey } from "./Api/tenantDetailsapi";
import OverviewCardHeader from "../Overview/OverviewCardHeader";
import EndpointActionButton from "../Endpoints/EndpointActionButton";

function TenantStat({ icon: Icon, label, value, tone }: { icon: LucideIcon; label: string; value: ReactNode; tone?: string }) {
    return (
        <Card className="overview-card tenant-stat" data-tone={tone}>
            <span className="tenant-stat-icon" aria-hidden="true"><Icon /></span>
            <div className="min-w-0">
                <span className="tenant-stat-value">{value}</span>
                <span className="tenant-stat-label">{label}</span>
            </div>
        </Card>
    );
}

function TenantField({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children: ReactNode }) {
    return (
        <div className="tenant-field">
            <span className="tenant-field-icon" aria-hidden="true"><Icon /></span>
            <div className="min-w-0">
                <span className="tenant-field-label">{label}</span>
                <div className="tenant-field-value">{children}</div>
            </div>
        </div>
    );
}

export default function TenantDetailsPage() {
    const [tenantData, setTenantData] = useState<TenantData[]>([]);
    const [deleteApiKeyDialog, setDeleteApiKeyDialog] = useState(false);
    const [deleteTenantDialog, setDeleteTenantDialog] = useState(false);
    const [isApiKeyVisible, setIsApiKeyVisible] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const { toast } = useToast();
    const userEmail = useUserStore((state) => state.user.email);
    const [, setTimers] = useState<Record<string, number>>({});

    const handleCopyApiKey = (apiKey: string) => {
        navigator.clipboard.writeText(apiKey);
        setIsCopied(true);

        setTimeout(() => {
            setIsCopied(false);
        }, 2000);

        toast({
            title: "Agent Key Copied",
            description: "The agent key has been copied to your clipboard.",
        });
    };

    const toggleApiKeyVisibility = () => {
        setIsApiKeyVisible(!isApiKeyVisible);
    };

    const handleGenerateApiKey = async (tenant: TenantData) => {
        try {
            const response = await generateApiKey(tenant.tenantName);

            const updatedTenantData = tenantData.map((el) => {
                if (el.tenantName === tenant.tenantName) {
                    console.log(response.key, response.expiredAt, el);
                    return {
                        ...el,
                        apiKey: { key: response.key, expiredAt: response.expiredAt },
                    };
                } else {
                    return el;
                }
            });

            setTenantData(updatedTenantData);

            toast({
                title: "Agent Key Generated",
                description: `New agent key has been generated for ${tenant.tenantName}.`,
            });
        } catch (error) {
            console.error("Error generating API key:", error);
            toast({
                title: "Error",
                description: "Failed to generate API key. Please try again.",
                variant: "destructive",
            });
        }
    };


    const formatCountdown = (expiry: string | null) => {
        if (!expiry) return "No key";

        const expiryDate = new Date(expiry);
        const now = new Date();
        const diffMs = expiryDate.getTime() - now.getTime();

        if (diffMs <= 0) return "Expired";

        const totalSeconds = Math.floor(diffMs / 1000);
        const days = Math.floor(totalSeconds / (3600 * 24));
        const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return `${days}d ${hours.toString().padStart(2, "0")}h ${minutes
            .toString()
            .padStart(2, "0")}m ${seconds.toString().padStart(2, "0")}s`;
    };



    const handleDeleteApiKeyConfirm = async (tenant: TenantData) => {

        await deleteTenant(tenant.tenantName, tenant.apiKey?.key || "");

        toast({
            title: "Agent Key Deleted",
            description: `Agent key for ${tenant.tenantName} has been revoked and is no longer valid.`,
            variant: "destructive",
        });
        setTenantData((prevTenants) =>
            prevTenants.map((t) =>
                t.tenantName === tenant.tenantName
                    ? { ...t, apiKey: null }
                    : t
            )
        );
        setDeleteApiKeyDialog(false);
    };

    const fetchTenantsDetails = async () => {
        let response = await fetchTenants();
        setTenantData(response || []);
    }

    useEffect(() => {
        const interval = setInterval(() => {
            setTimers((prev) => {
                const updated: Record<string, number> = {};
                tenantData.forEach((tenant) => {
                    const expiry = tenant.apiKey?.expiredAt;
                    if (expiry) {
                        const expiryDate = new Date(expiry);
                        updated[tenant.domainName] = expiryDate.getTime();
                    }
                });
                return updated;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [tenantData]);

    useEffect(() => {
        fetchTenantsDetails();
    }, [userEmail]);


    return (
        <div className="endpoint-overview tenant-details-page">
            {tenantData.map((tenant) => {
                const countdown = formatCountdown(tenant.apiKey?.expiredAt ?? null);
                const keyStatus = !tenant.apiKey?.key
                    ? "Not configured"
                    : countdown === "Expired"
                        ? "Expired"
                        : "Active";

                return (
                    <Fragment key={tenant.tenantName}>
                        <Card className="overview-card tenant-hero">
                            <div className="tenant-identity">
                                <span className="tenant-avatar" aria-hidden="true">
                                    <Building2 />
                                </span>
                                <div className="min-w-0">
                                    <h1 className="tenant-name" data-testid="text-tenant-name">
                                        {tenant.tenantName}
                                    </h1>
                                    <p className="tenant-sub">Manage tenant configuration and agent access</p>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => setDeleteTenantDialog(true)}
                                className="tenant-danger-button no-default-hover-elevate"
                                data-testid="button-delete-tenant"
                                disabled
                            >
                                <Trash2 />
                                Delete Tenant
                            </Button>
                        </Card>

                        <div className="tenant-stat-grid">
                            <TenantStat icon={Monitor} label="Computers" value={tenant.numberOfComputers} />
                            <TenantStat icon={Users} label="Users" value={tenant.numberOfUsers} tone="violet" />
                            <TenantStat
                                icon={Key}
                                label="Agent Key"
                                value={keyStatus}
                                tone={keyStatus === "Active" ? "success" : keyStatus === "Expired" ? "danger" : "muted"}
                            />
                        </div>

                        <div className="tenant-grid">
                            <Card className="overview-card">
                                <OverviewCardHeader
                                    fromdetails
                                    title="Tenant Overview"
                                    description="Identity and inventory for this tenant"
                                    icon={Building2}
                                />
                                <CardContent className="overview-card-content tenant-fields">
                                    <TenantField icon={Building2} label="Tenant Name">
                                        {tenant.tenantName}
                                    </TenantField>
                                    <TenantField icon={Monitor} label="Computers">
                                        {tenant.numberOfComputers}
                                    </TenantField>
                                    <TenantField icon={Users} label="Users">
                                        {tenant.numberOfUsers}
                                    </TenantField>
                                </CardContent>
                            </Card>

                            <Card className="overview-card tenant-key-card">
                                <OverviewCardHeader
                                    fromdetails
                                    title="Agent Key"
                                    description="Used by endpoint agents to register with this tenant"
                                    icon={Key}
                                />
                                <CardContent className="overview-card-content tenant-key-content">
                                    {tenant.apiKey?.key ? (
                                        <>
                                            <div className="tenant-key-field">
                                                <span className="tenant-field-label">Key</span>
                                                <div className="tenant-key-row">
                                                    <code className="tenant-key-value">
                                                        {isApiKeyVisible ? tenant.apiKey.key : '••••••'}
                                                    </code>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="tenant-icon-button no-default-hover-elevate"
                                                        onClick={toggleApiKeyVisibility}
                                                        title={isApiKeyVisible ? "Hide agent key" : "Show agent key"}
                                                        aria-label={isApiKeyVisible ? "Hide agent key" : "Show agent key"}
                                                    >
                                                        {isApiKeyVisible ? <EyeOff /> : <Eye />}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="tenant-icon-button no-default-hover-elevate"
                                                        onClick={() => handleCopyApiKey(tenant.apiKey?.key!)}
                                                        title="Copy agent key"
                                                        aria-label="Copy agent key"
                                                        data-testid="button-copy-api-key"
                                                    >
                                                        {isCopied ? <Check className="tenant-copied" /> : <Copy />}
                                                    </Button>
                                                </div>
                                            </div>

                                            {tenant.apiKey.expiredAt && (
                                                <div className="tenant-key-field">
                                                    <span className="tenant-field-label">Expires In</span>
                                                    <span className="tenant-expiry">
                                                        <Clock aria-hidden="true" />
                                                        {countdown}
                                                    </span>
                                                </div>
                                            )}

                                            <Button
                                                variant="outline"
                                                onClick={() => setDeleteApiKeyDialog(true)}
                                                className="tenant-danger-button tenant-key-action no-default-hover-elevate"
                                                data-testid="button-delete-api-key"
                                            >
                                                <Trash2 />
                                                Delete Agent Key
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="tenant-key-empty">
                                                <span className="tenant-key-empty-icon" aria-hidden="true">
                                                    <Key />
                                                </span>
                                                <p className="tenant-key-empty-title">No agent key configured</p>
                                                <p className="tenant-key-empty-text">Generate a key to enable agent access</p>
                                            </div>
                                            <EndpointActionButton
                                                onClick={() => handleGenerateApiKey(tenant)}
                                                className="tenant-key-action"
                                                data-testid="button-generate-api-key"
                                            >
                                                <Key className="w-4 h-4" />
                                                Generate Agent Key
                                            </EndpointActionButton>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        <AlertDialog open={deleteApiKeyDialog} onOpenChange={setDeleteApiKeyDialog}>
                            <AlertDialogContent className="tenant-dialog">
                                <AlertDialogHeader className="tenant-dialog-header">
                                    <span className="tenant-dialog-icon" aria-hidden="true">
                                        <Key />
                                    </span>
                                    <div className="min-w-0">
                                        <AlertDialogTitle className="tenant-dialog-title">Delete Agent Key</AlertDialogTitle>
                                        <AlertDialogDescription className="tenant-dialog-description">
                                            Are you sure you want to delete the agent key for "{tenant.tenantName}"?
                                            This action cannot be undone and will immediately revoke access for all agents using this key.
                                        </AlertDialogDescription>
                                    </div>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="tenant-dialog-footer">
                                    <AlertDialogCancel className="tenant-dialog-button">Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={() => handleDeleteApiKeyConfirm(tenant)}
                                        className="tenant-dialog-button bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                        Delete Agent Key
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>

                        <AlertDialog open={deleteTenantDialog} onOpenChange={setDeleteTenantDialog}>
                            <AlertDialogContent className="tenant-dialog">
                                <AlertDialogHeader className="tenant-dialog-header">
                                    <span className="tenant-dialog-icon" aria-hidden="true">
                                        <Trash2 />
                                    </span>
                                    <div className="min-w-0">
                                        <AlertDialogTitle className="tenant-dialog-title">Delete Tenant</AlertDialogTitle>
                                        <AlertDialogDescription className="tenant-dialog-description">
                                            Are you sure you want to permanently delete the tenant "{tenant.tenantName}"?
                                            This action cannot be undone and will remove all associated data, users, and devices.
                                        </AlertDialogDescription>
                                    </div>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="tenant-dialog-footer">
                                    <AlertDialogCancel className="tenant-dialog-button">Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={() => { }}
                                        className="tenant-dialog-button bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                        Delete Tenant
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </Fragment>
                );
            })}
        </div>
    );
}
